import type { Config } from '@gmn-portal/backend';
import type { Where } from '@/core/api';

/**
 * Collections whose human-facing label is composed of more than the single
 * `searchKey` field. Users are listed by full name (`firstName lastName`) but
 * were only ever searched by `email`, so typing a name returned nothing.
 * Searching the name fields plus email keeps results aligned with the label.
 */
const COLLECTION_SEARCH_KEYS: Partial<Record<keyof Config['collections'], string[]>> = {
  users: ['firstName', 'lastName', 'email'],
};

/**
 * Resolve which fields a combobox should search for a given collection. Falls
 * back to the explicit `searchKey` for collections without a special mapping
 * (customers, objects, object-categories, … search their `name`).
 */
export const getSearchKeys = <T extends keyof Config['collections']>(
  collectionKey: T,
  searchKey: keyof Config['collections'][T]
): string[] => COLLECTION_SEARCH_KEYS[collectionKey] ?? [String(searchKey)];

/**
 * Archived users must never appear in a picker. Returns the visibility
 * constraint for the collection, or `null` when none applies.
 */
export const buildUserVisibilityWhere = <T extends keyof Config['collections']>(
  collectionKey: T
): Where | null => (collectionKey === 'users' ? { archived: { not_equals: true } } : null);

/**
 * Build the search portion of a combobox `where` clause. The query is split on
 * whitespace; every token must match at least one of the search fields (AND
 * across tokens, OR across fields). This lets "Holger Zimmermann" match a user
 * whose first name is "Holger" and last name is "Zimmermann" while still
 * matching single-token queries against any field.
 */
export const buildSearchConditions = (searchKeys: string[], query: string): Where[] => {
  const tokens = query.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  return tokens.map((token) => ({
    or: searchKeys.map((key) => ({ [key]: { like: token } })),
  }));
};
